import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import { useNotification } from '../context/NotificationContext';

const ConfirmEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const token = searchParams.get('token');
  const [step, setStep] = useState('confirming'); // confirming, password, error
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStep('error');
      notify.error(t('login.noConfirmationToken'));
      return;
    }
    confirmEmail();
    // Runs once per token: confirmEmail POSTs the confirmation, so re-running
    // it when `notify`/`t` change would confirm the same token twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const confirmEmail = async () => {
    try {
      await authService.confirmEmail(token);
      setStep('password');
      notify.success(t('login.emailConfirmed'));
    } catch (err) {
      setStep('error');
      notify.error(err.response?.data?.detail || t('login.failedToConfirmEmail'));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await authService.setPassword(token, password);
      notify.success(t('login.passwordSetSuccess'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      notify.error(err.response?.data?.detail || t('login.failedToSetPassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h2 style={{ textAlign: 'center', color: '#3D3D3D', marginBottom: '30px' }}>
          Confirm Email & Set Password
        </h2>

        {step === 'confirming' && (
          <div className="alert alert-warning text-center">
            <p>{t('login.confirmingEmail')}</p>
          </div>
        )}

        {step === 'password' && (
          <>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
                  Password (min 8 characters)
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.password ? '2px solid #F22B55' : '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.password && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.password}</p>}
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.confirmPassword ? '2px solid #F22B55' : '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.confirmPassword && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#A0BD46',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Setting password...' : 'Complete Registration'}
              </button>

              <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px' }}>
                <a href="/login" style={{ color: '#20BDE2', textDecoration: 'none' }}>
                  Back to login
                </a>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmailPage;
